import { Button, Checkbox, TextField } from "@ths/ui";
import { useRef, useState } from "react";

import type { CheckoutReminderPreferencesQuery } from "../generated/graphql";
import {
  formatPhoneInput,
  getRequiredPhoneValidationError,
  normalizePhoneForSubmit,
} from "../steps/phoneFormatting";

import {
  ReminderConsentFields,
  type ReminderOptions,
} from "./ReminderConsentFields";

export type ReminderPreference =
  CheckoutReminderPreferencesQuery["checkoutReminderPreferences"];

export function ReminderPreferenceForm({
  current,
  options,
  prompt = false,
  requirePhone = false,
  onSave,
  onDismiss,
  onSavingChange,
}: {
  current: ReminderPreference;
  options: ReminderOptions;
  prompt?: boolean;
  requirePhone?: boolean;
  onSave: (
    sms: boolean | undefined,
    email: boolean,
    phone?: string,
  ) => Promise<void>;
  onDismiss?: () => void;
  onSavingChange?: (saving: boolean) => void;
}) {
  const [sms, setSms] = useState(
    current.smsEnabled && !current.smsBlockedReason,
  );
  const checkoutOnly =
    current.smsEnabled && current.smsConsentScope !== "LODGING";
  const [expandConsent, setExpandConsent] = useState(false);
  const wantsSms = sms || expandConsent;
  const [email, setEmail] = useState(
    current.emailEnabled && !current.emailBlockedReason,
  );
  const [phone, setPhone] = useState("");
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const saveLock = useRef(false);
  const needsPhone =
    requirePhone ||
    Boolean(getRequiredPhoneValidationError(current.phone ?? ""));
  const collectPhone =
    options.smsAvailable && !current.smsBlockedReason && needsPhone;
  const phoneError = getRequiredPhoneValidationError(phone);

  return (
    <form
      noValidate
      className="space-y-6"
      onSubmit={async (event) => {
        event.preventDefault();
        if (saveLock.current || (prompt && !wantsSms)) return;
        setPhoneTouched(true);
        if (collectPhone && wantsSms && phoneError) return;
        saveLock.current = true;
        setSaving(true);
        onSavingChange?.(true);
        setError("");
        setSaved(false);
        try {
          await onSave(
            checkoutOnly && !expandConsent && sms ? undefined : wantsSms,
            email,
            collectPhone && wantsSms
              ? (normalizePhoneForSubmit(phone) ?? undefined)
              : undefined,
          );
          setSms(wantsSms);
          setExpandConsent(false);
          setSaved(true);
        } catch (cause) {
          setError(
            cause instanceof Error
              ? cause.message
              : "We could not save your preferences. Please try again.",
          );
        } finally {
          saveLock.current = false;
          setSaving(false);
          onSavingChange?.(false);
        }
      }}
    >
      {collectPhone && (
        <div className="space-y-2 [&_label]:text-on-navy/80 [&_p]:text-error-surface">
          <TextField
            label="Mobile number"
            type="tel"
            autoComplete="tel"
            inputMode="tel"
            required={wantsSms}
            value={phone}
            maxLength={24}
            placeholder="(555) 555-5555"
            onChange={(event) => {
              setPhone(formatPhoneInput(event.target.value));
              setSaved(false);
            }}
            onBlur={() => setPhoneTouched(true)}
            disabled={saving}
            error={phoneTouched ? phoneError : undefined}
            className="rounded-xl border-on-navy/25 bg-on-navy/5 text-on-navy placeholder:text-on-navy/50 focus-visible:ring-on-navy"
          />
          <span className="block text-xs text-on-navy/70">
            Use a US mobile number.
          </span>
        </div>
      )}
      {options.smsAvailable && (
        <div className="space-y-3">
          {checkoutOnly && (
            <div className="space-y-2 [&_label]:text-on-navy">
              <Checkbox
                label="Keep my checkout reminder texts"
                checked={sms || expandConsent}
                disabled={
                  saving || expandConsent || Boolean(current.smsBlockedReason)
                }
                onCheckedChange={(value) => {
                  setSms(value);
                  setSaved(false);
                }}
              />
              <p className="text-sm text-on-navy/70">
                You agreed to checkout reminders only. Select the option below
                to also receive other lodging updates.
              </p>
            </div>
          )}
          <ReminderConsentFields
            embedded
            options={options}
            checked={checkoutOnly ? expandConsent : sms}
            disabled={saving || Boolean(current.smsBlockedReason)}
            onCheckedChange={(value) => {
              if (checkoutOnly) setExpandConsent(value);
              else setSms(value);
              setSaved(false);
            }}
          />
          {current.smsBlockedReason && (
            <p role="status" className="text-sm text-on-navy/70">
              Texts stopped: {current.smsBlockedReason}. Contact Basecamp staff
              for help.
            </p>
          )}
        </div>
      )}
      {!prompt && options.emailAvailable && (
        <section
          className="space-y-2 [&>label]:min-h-11 [&>label]:text-on-navy"
          aria-label="Email reminders"
        >
          <Checkbox
            checked={email}
            disabled={saving || Boolean(current.emailBlockedReason)}
            onCheckedChange={(value) => {
              setEmail(value);
              setSaved(false);
            }}
            label="Email me checkout reminders"
            className="border-on-navy/50 bg-on-navy/5 focus-visible:ring-on-navy"
          />
          {current.emailBlockedReason && (
            <p role="status" className="text-sm text-on-navy/70">
              Emails stopped: {current.emailBlockedReason}. Contact Basecamp
              staff for help.
            </p>
          )}
        </section>
      )}
      {error && (
        <p role="alert" className="text-sm text-error-surface">
          {error}
        </p>
      )}
      {saved && (
        <p role="status" className="text-sm text-on-navy/80">
          Preferences saved.
        </p>
      )}
      <div className="space-y-2">
        <Button
          type="submit"
          fullWidth
          variant="ghost"
          disabled={saving || (prompt && !wantsSms)}
          className="min-h-12 rounded-xl border border-on-navy/25 bg-on-navy/10 text-on-navy hover:bg-on-navy/15 focus-visible:ring-on-navy"
        >
          {saving ? "Saving…" : prompt ? "Turn on texts" : "Save changes"}
        </Button>
        {prompt && (
          <Button
            type="button"
            fullWidth
            variant="ghost"
            disabled={saving}
            onClick={onDismiss}
            className="min-h-11 text-on-navy/70 hover:bg-on-navy/5 focus-visible:ring-on-navy"
          >
            No thanks
          </Button>
        )}
      </div>
    </form>
  );
}
